<template>
  <div class="sp-card">

    <!-- Header -->
    <header class="sp-hdr">
      <span class="sp-hdr__rule" aria-hidden="true"></span>
      <span class="sp-hdr__title">Listing Summary</span>
      <span class="sp-hdr__sep"></span>

      <!-- Unit info varies per route -->
      <span class="sp-hdr__unit" v-if="$route.path == '/mls-list'">
        Unit <b>{{ data.field_mls_address_value }}</b>
      </span>
      <span class="sp-hdr__unit" v-else-if="$route.path == '/unit-report'">
        Unit
        <b v-if="data.floor_plan && data.floor_plan.fid" v-b-modal="'mls-fp-' + data.floor_plan.fid"
          class="sp-unit-link">{{ data.field_mls_address_value }}</b>
        <b v-else>{{ data.field_mls_address_value }}</b>
      </span>
      <span class="sp-hdr__unit" v-else-if="$route.path == '/type'">
        Unit
        <b v-if="data[data.length - 1].mls_floor_plan_feature.fid"
          v-b-modal="'mls-fp-' + data[data.length - 1].mls_floor_plan_feature.fid" class="sp-unit-link">{{
            data[data.length - 1].mls_address }}</b>
        <b v-else>{{ data[data.length - 1].mls_address }}</b>
        <template v-if="data[data.length - 1].mls_plan_name"> · Plan <b>{{ data[data.length - 1].mls_plan_name
        }}</b></template>
      </span>
      <span class="sp-hdr__unit" v-else-if="$route.path == '/rental-report'">
        Unit
        <b v-if="data.floor_plan" v-b-modal="'mls-fp-' + data.floor_plan.fid" class="sp-unit-link">{{
          data.field_mls_address_value }}</b>
        <b v-else>{{ data.field_mls_address_value }}</b>
        <template v-if="data.field_mls_plan_name_value"> · Plan <b>{{ data.field_mls_plan_name_value }}</b></template>
      </span>
      <span class="sp-hdr__unit" v-else>
        Unit
        <b v-if="data[data.length - 1].mls_floor_plan_feature.fid"
          v-b-modal="'mls-fp-' + data[data.length - 1].mls_floor_plan_feature.fid" class="sp-unit-link">{{
            data[data.length - 1].mls_address }}</b>
        <b v-else>{{ data[data.length - 1].mls_address }}</b>
      </span>

      <!-- Date varies per route -->
      <div class="sp-hdr__date">
        <template v-if="['/mls-list', '/unit-report'].includes($route.path)">
          <span class="sp-hdr__date-lbl">List Date</span>
          <span class="sp-hdr__date-val">{{
            data.field_mls_list_date_value != '0' ? data.field_mls_list_date_value : '—'
          }}</span>
        </template>
        <template v-else-if="$route.path == '/rental-report'">
          <span class="sp-hdr__date-lbl">Sold Date</span>
          <span class="sp-hdr__date-val">{{ data.sold_date || '—' }}</span>
        </template>
        <template v-else>
          <span class="sp-hdr__date-lbl">List Date</span>
          <span class="sp-hdr__date-val">{{
            data[data.length - 1].mls_list_date != '0' ? data[data.length - 1].mls_list_date : '—'
          }}</span>
        </template>
      </div>

      <!-- Add Entry button (admin only, on /type and default/UTC routes) -->
      <button
        v-if="current_user.role == 'admin' && !['/mls-list', '/unit-report', '/rental-report'].includes($route.path)"
        class="sp-add-entry" v-b-modal="'mls-entry-new'" @click="$emit('set-project-id', data[data.length - 1].cid)">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
          stroke-linejoin="round" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Add Entry
      </button>

      <!-- Close button -->
      <button class="sp-hdr__close" type="button" aria-label="Close" @click="$emit('close')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </header>

    <!-- Specs strip — /mls-list & /unit-report (single-object data) -->
    <div class="sp-specs" v-if="['/mls-list', '/unit-report'].includes($route.path)">
      <div class="sp-spec">
        <span class="sp-spec__lbl">Beds</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.field_mls_beds_value }">{{
          data.field_mls_beds_value || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Baths</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.field_mls_baths_value }">{{
          data.field_mls_baths_value || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Size</span>
        <span class="sp-spec__val">{{ Math.round(data.field_mls_unit_size_value).toLocaleString() }}<span
            class="sp-spec__muted"> sqft</span></span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">FP Specs</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.fp_specs }">{{ data.fp_specs || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">View</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.view_orientation }">{{ data.view_orientation || '—'
        }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Levels</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.floor_levels }">{{ data.floor_levels || '—'
        }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Parking</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.parking }">{{ data.parking || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Stalls</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.stalls }">{{ data.stalls || '—' }}</span>
      </div>
    </div>

    <!-- Specs strip — /type (array data) -->
    <div class="sp-specs sp-specs--wide" v-else-if="$route.path == '/type'">
      <div class="sp-spec">
        <span class="sp-spec__lbl">Plan</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].mls_plan_name }">{{
          data[data.length - 1].mls_plan_name || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Beds</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].mls_beds }">{{ data[data.length -
          1].mls_beds || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Baths</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].mls_baths }">{{ data[data.length -
          1].mls_baths || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Size</span>
        <span class="sp-spec__val">{{ Math.round(data[data.length - 1].mls_unit_size).toLocaleString() }}<span
            class="sp-spec__muted"> sqft</span></span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">FP Specs</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].fp_specs }">{{ data[data.length -
          1].fp_specs || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">View</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].orientation }">{{ data[data.length
          - 1].orientation || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Levels</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].levels }">{{ data[data.length -
          1].levels || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Parking</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].stalls }">{{ data[data.length -
          1].stalls || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Stalls</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].mls_parking }">{{ data[data.length
          - 1].mls_parking || '—' }}</span>
      </div>
      <div class="sp-spec sp-spec--wide">
        <span class="sp-spec__lbl">Balcony</span>
        <span class="sp-spec__val">
          <template v-if="data[data.length - 1].balcony && data[data.length - 1].balcony !== 'None'">
            {{ data[data.length - 1].balcony
            }}<template v-if="data[data.length - 1].balcony_area"> · {{ data[data.length - 1].balcony_area }}<span
                class="sp-spec__muted"> sqft</span></template>
          </template>
          <span v-else class="sp-spec__muted">None</span>
        </span>
      </div>
    </div>

    <!-- Specs strip — /rental-report (single-object data) -->
    <div class="sp-specs sp-specs--wide" v-else-if="$route.path == '/rental-report'">
      <div class="sp-spec">
        <span class="sp-spec__lbl">Plan</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.field_mls_plan_name_value }">{{
          data.field_mls_plan_name_value || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Beds</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.field_mls_beds_value }">{{
          data.field_mls_beds_value || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Baths</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.field_mls_baths_value }">{{
          data.field_mls_baths_value || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Size</span>
        <span class="sp-spec__val">{{ Math.round(data.field_mls_unit_size_value).toLocaleString() }}<span
            class="sp-spec__muted"> sqft</span></span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">FP Specs</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.fp_specs }">{{ data.fp_specs || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">View</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.view_orientation }">{{ data.view_orientation || '—'
        }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Levels</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.floor_levels }">{{ data.floor_levels || '—'
        }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Parking</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.stalls }">{{ data.stalls || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Stalls</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data.parking }">{{ data.parking || '—' }}</span>
      </div>
      <div class="sp-spec sp-spec--wide">
        <span class="sp-spec__lbl">Balcony</span>
        <span class="sp-spec__val">
          <template v-if="data.balcony && data.balcony !== 'None'">
            {{ data.balcony }}<template v-if="data.balcony_area"> · {{ data.balcony_area }}<span class="sp-spec__muted">
                sqft</span></template>
          </template>
          <span v-else class="sp-spec__muted">None</span>
        </span>
      </div>
    </div>

    <!-- Specs strip — default (UTC, array data) -->
    <div class="sp-specs" v-else>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Beds</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].mls_beds }">{{ data[data.length -
          1].mls_beds || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Baths</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].mls_baths }">{{ data[data.length -
          1].mls_baths || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Size</span>
        <span class="sp-spec__val">{{ Math.round(data[data.length - 1].mls_unit_size).toLocaleString() }}<span
            class="sp-spec__muted"> sqft</span></span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">FP Specs</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].fp_specs }">{{ data[data.length -
          1].fp_specs || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">View</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].orientation }">{{ data[data.length
          - 1].orientation || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Levels</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].levels }">{{ data[data.length -
          1].levels || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Parking</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].stalls }">{{ data[data.length -
          1].stalls || '—' }}</span>
      </div>
      <div class="sp-spec">
        <span class="sp-spec__lbl">Stalls</span>
        <span class="sp-spec__val" :class="{ 'sp-spec__muted': !data[data.length - 1].mls_parking }">{{ data[data.length
          - 1].mls_parking || '—' }}</span>
      </div>
    </div>

    <!-- Unified table -->
    <div class="sp-table-wrap">
      <b-table :items="computed_mlsHistory" :fields="custom_header_fields" class="sp-table" responsive hover small>
        <template #head(previous_net_price)="data">
          <div class="d-inline-flex align-items-center" style="white-space: nowrap; gap: 4px;">
            Δ Net
          </div>
        </template>
        <template #cell(mls_psf)="tableData">
          <span :style="{ color: setColor(tableData.item.price_back_up) }">
            {{
              calculateResult(tableData.item) > 0
                ? "$" + calculateResult(tableData.item)
                : "—"
            }}
          </span>
        </template>
        <template #cell(mls_status)="tableData">
          <span class="sp-status" :class="lsmStatusClass(tableData.value)">{{ tableData.value || '—' }}</span>
        </template>
        <template #cell(modified_listing_type)="data">
          <span v-if="data.value" :class="listingTypePillClass(data.value)">
            <span class="rpd-lt-pill__dot"></span>{{ data.value }}
          </span>
          <span v-else>&#8212;</span>
        </template>
        <template #cell(net_psf)="data">
          <span>
            {{ calculateNetPsf(data.item) }}
          </span>
        </template>
        <template #cell(mls_sold_price)="data">
          <span :style="{ color: setColor(data.item.price_back_up) }">{{
            data.value > 0
              ? "$" + Math.round(data.value).toLocaleString()
              : "—"
          }}</span>
        </template>
        <template #cell(buyer_incentives)="data">
          <span v-if="data.value > 0">{{
            "$" + Math.round(data.value).toLocaleString()
          }}</span>
          <span v-else class="sp-muted">—</span>
        </template>
        <template #cell(net_price)="data">
          <b>{{
            getNetPrice(data.item) != "-"
              ? "$" + getNetPrice(data.item).toLocaleString()
              : "—"
          }}</b>
        </template>
        <template #cell(list_price)="data">
          <span>{{ data.value ? "$" + Math.round(data.value).toLocaleString() : '—' }}</span>
        </template>
        <template #cell(sold_price)="data">
          <span>{{ data.value ? "$" + Math.round(data.value).toLocaleString() : '—' }}</span>
        </template>
        <template #cell(previous_net_price)="data">
          <template v-if="
            ![0, '-', null, NaN].includes(
              Number(
                getPriceChange(data.value, getNetPrice(data.item))
                  .diffPercentage
              )
            )
          ">
            <span class="sp-delta" :class="Number(getPriceChange(data.value, getNetPrice(data.item)).diffPercentage) < 0
              ? 'sp-delta--down' : 'sp-delta--up'">
              <svg v-if="Number(getPriceChange(data.value, getNetPrice(data.item)).diffPercentage) < 0"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"
                stroke-linejoin="round">
                <path d="M12 5v14" />
                <path d="M19 12l-7 7-7-7" />
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"
                stroke-linejoin="round">
                <path d="M12 19V5" />
                <path d="M5 12l7-7 7 7" />
              </svg>
              {{
                Number(
                  getPriceChange(data.value, getNetPrice(data.item))
                    .diffPercentage
                ).toLocaleString()
              }}%
            </span>
          </template>
          <span v-else class="sp-muted">—</span>
        </template>
        <template #cell(mls_list_price)="data">
          <span :style="{ color: setColor(data.item.price_back_up) }">{{
            data.value > 0
              ? "$" + Math.round(data.value).toLocaleString()
              : "—"
          }}</span>
        </template>
        <template #cell(action)="data">
          <span class="sp-action sp-action--edit" title="Edit MLS Entry" @click="
            $emit('editMLSEntryNew', {
              projectId: data.item.cid,
              projectEntry: data.item.entry,
              rowData: data.item,
              listingType: listing_type
            })
            ">
            <font-awesome-icon :icon="['fas', 'edit']" />
          </span>
          <span class="sp-action-sep">|</span>
          <span class="sp-action sp-action--del" title="Delete MLS Entry" @click="
            $emit('deleteMLSEntry', {
              projectId: data.item.cid,
              projectEntry: data.item.entry,
              rowData: data.item,
              listingType: listing_type
            })
            ">
            <font-awesome-icon :icon="['fas', 'trash']" />
          </span>
        </template>
        <template #cell(backup)="data">
          <a v-if="data.item.backup_file" :href="data.item.backup_file" target="_blank" class="sp-backup-link"
            title="View Backup File">
            <font-awesome-icon :icon="['fas', 'file-alt']" />
          </a>
          <span v-else class="sp-muted">—</span>
        </template>
        <template #cell(mls_need_approval)="data">
          <button class="sp-approve-btn">
            {{ Number(data.value) > 0 ? "Approve" : "UnApprove" }}
          </button>
        </template>
        <template #cell(backup_file)="data">
          <a v-if="data.value" :href="data.value" target="_blank" class="sp-backup-link" title="View Backup File">
            <font-awesome-icon :icon="['fas', 'file-alt']" />
          </a>
          <span v-else class="sp-muted">—</span>
        </template>
        <template #cell()="data">
          <span v-if="$route.path == '/rental-report'">{{ getDefaultValue(data) }}</span>
          <span v-else>{{ data.value ? data.value : "—" }}</span>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BBadge } from "bootstrap-vue";
import { ref, onMounted, computed } from "vue";
import { useUserStore } from "../../store/UserStore";
import { useRouter } from "vue2-helpers/vue-router";
import { listingTypePillClass } from "../../composables/listingTypePill";

const route = useRouter();
const current_user = ref(useUserStore().current_user);

const props = defineProps({
  data: null,
  mlsHistory: null,
  listing_type: null,
  project_status: null,
  header_fields: null,
  occupancy_date: null,
  sold_out_date: null,
  isMarketStrata: null,
  reportHistoryData: null,
});

const mlsHistory_model = computed(() => props.mlsHistory);

const computed_mlsHistory = computed(() => {
  return props.mlsHistory
    ? props.mlsHistory.map((history, index) => {
      var rowColor = history.status == 'Active' ? 'success' : history.status == 'Sold' ? 'danger' : 'white'
      return {
        ...history,
        _rowVariant: props.listing_type === 'all' ? history._rowVariant : rowColor,
        previous_net_price:
          index > 0 ? getNetPrice(props.mlsHistory[index - 1]) : 0,
      };
    })
    : [];
  // if(props.mlsHistory != null)
  //     return current_user.value.role != 'admin' ? props.mlsHistory.filter(x => x.mls_list_price != '-') : props.mlsHistory
});

let custom_header_fields = ref();

const setHeaderFields = () => {
  switch (route.currentRoute.path) {
    case "/mls-list":
      if (current_user.value.role == "admin") {
        custom_header_fields.value = [
          {
            label: "Listing Type",
            key: "modified_listing_type",
            formatter: setListingType,
          },
          { label: "Status", key: "mls_status", thClass: "w-small" },
          { label: "List Price", key: "mls_list_price" },
          { label: "Beds", key: "mls_beds" },
          { label: "Baths", key: "mls_baths" },
          { label: "$PSF", key: "mls_psf" },
          { label: "Sold Price", key: "mls_sold_price" },
          { label: "SP SQFT", key: "mls_sp_sqft" },
          { label: "Sold Date", key: "mls_sold_date" },
          { label: "Collection Type", key: "collection_type" },
        ];
      } else {
        custom_header_fields.value = [
          {
            label: "Listing Type",
            key: "modified_listing_type",
            formatter: setListingType,
          },
          { label: "Status", key: "mls_status", thClass: "w-small" },
          { label: "List Price", key: "mls_list_price" },
          { label: "$PSF", key: "mls_psf" },
          { label: "Price Date", key: "price_date" },
          { label: "Incentives", key: "buyer_incentives" },
          { label: "Net Price", key: "net_price" },
          { label: "Net $PSF", key: "net_psf" },
          { label: "DOM", key: "mls_dom" },
          { label: "Sold Date", key: "mls_sold_date" },
        ];
      }
      break;

    case "/type-old":
    case "/type":
      if (props.listing_type == 'all') {
        switch (props.project_status.toLowerCase()) {
          case "now leasing":
            if (current_user.value.role == "admin") {
              custom_header_fields.value = [
                {
                  label: "Listing Type",
                  key: "modified_listing_type",
                  formatter: setListingType,
                },
                { label: "Status", key: "mls_status", thClass: "w-small" },
                { label: "DOM", key: "mls_dom" },
                { label: "List Price", key: "mls_list_price" },
                { label: "$PSF", key: "mls_psf" },
                { label: "Incentives", key: "buyer_incentives" },
                { label: "Net Price", key: "net_price" },
                { label: "Net $PSF", key: "net_psf" },
                { label: "Net Price", key: "previous_net_price" },
                { label: "Price Date", key: "mls_price_date" },
                { label: "Sold Date", key: "mls_sold_date" },
                { label: "Action", key: "action" },
                { label: "Backup File", key: "backup" },
                { label: "Last Modified", key: "last_modified" },
              ];
            } else {
              custom_header_fields.value = [
                {
                  label: "Listing Type",
                  key: "modified_listing_type",
                  formatter: setListingType,
                },
                { label: "Status", key: "mls_status", thClass: "w-small" },
                { label: "Price Date", key: "mls_price_date" },
                { label: "DOM", key: "mls_dom" },
                { label: "List Price", key: "mls_list_price" },
                { label: "$PSF", key: "mls_psf" },
                { label: "Incentives", key: "buyer_incentives" },
                { label: "Net Price", key: "net_price" },
                { label: "Net Price", key: "previous_net_price" },
                { label: "Net $PSF", key: "net_psf" },
                { label: "Leased Date", key: "mls_sold_date" },
              ];
            }
            break;

          case "leased out":
            if (current_user.value.role == "admin") {
              custom_header_fields.value = [
                {
                  label: "Listing Type",
                  key: "modified_listing_type",
                  formatter: setListingType,
                },
                { label: "Status", key: "mls_status", thClass: "w-small" },
                { label: "DOM", key: "mls_dom" },
                { label: "List Price", key: "mls_list_price" },
                { label: "$PSF", key: "mls_psf" },
                { label: "Incentives", key: "buyer_incentives" },
                { label: "Net Price", key: "net_price" },
                { label: "Net $PSF", key: "net_psf" },
                { label: "Net Price", key: "previous_net_price" },
                { label: "Price Date", key: "mls_price_date" },
                { label: "Sold Date", key: "mls_sold_date" },
                { label: "Sold Price", key: "mls_sold_price" },
                { label: "Action", key: "action" },
                { label: "Backup File", key: "backup" },
                { label: "Last Modified", key: "last_modified" },
              ];
            } else {
              custom_header_fields.value = [
                {
                  label: "Listing Type",
                  key: "modified_listing_type",
                  formatter: setListingType,
                },
                { label: "Status", key: "mls_status", thClass: "w-small" },
                { label: "Price Date", key: "mls_price_date" },
                { label: "DOM", key: "mls_dom" },
                { label: "List Price", key: "mls_list_price" },
                { label: "$PSF", key: "mls_psf" },
                { label: "Incentives", key: "buyer_incentives" },
                { label: "Net Price", key: "net_price" },
                { label: "Net Price", key: "previous_net_price" },
                { label: "Net $PSF", key: "net_psf" },
                { label: "Leased Date", key: "mls_sold_date" },
              ];
            }
            break;

          default:
            if (current_user.value.role == "admin") {
              custom_header_fields.value = [
                {
                  label: "Listing Type",
                  key: "modified_listing_type",
                  formatter: setListingType,
                },
                { label: "Status", key: "mls_status", thClass: "w-small" },
                { label: "DOM", key: "mls_dom" },
                { label: "List Price", key: "mls_list_price" },
                { label: "$PSF", key: "mls_psf" },
                { label: "Incentives", key: "buyer_incentives" },
                { label: "Net Price", key: "net_price" },
                { label: "Net $PSF", key: "net_psf" },
                { label: "Net Price", key: "previous_net_price" },
                { label: "Price Date", key: "mls_price_date" },
                { label: "Sold Date", key: "mls_sold_date" },
                { label: "Sold Price", key: "mls_sold_price" },
                { label: "Action", key: "action" },
                { label: "Backup File", key: "backup" },
                { label: "Last Modified", key: "last_modified" },
              ];
            } else {
              custom_header_fields.value = [
                {
                  label: "Listing Type",
                  key: "modified_listing_type",
                  formatter: setListingType,
                },
                { label: "Status", key: "mls_status", thClass: "w-small" },
                { label: "Price Date", key: "mls_price_date" },
                { label: "DOM", key: "mls_dom" },
                { label: "List Price", key: "mls_list_price" },
                { label: "$PSF", key: "mls_psf" },
                { label: "Incentives", key: "buyer_incentives" },
                { label: "Net Price", key: "net_price" },
                { label: "Net Price", key: "previous_net_price" },
                { label: "Net $PSF", key: "net_psf" },
                { label: "Sold Date", key: "mls_sold_date" },
                { label: "Sold Price", key: "mls_sold_price" },
              ];
            }
            break;
        }
      } else {
        if (current_user.value.role == "admin") {
          custom_header_fields.value = [
            { label: "Status", key: "status", thClass: "w-small" },
            { label: "List Price", key: "list_price" },
            { label: "Price Date", key: "price_date" },
            { label: "Leased Price", key: "sold_price" },
            { label: "Leased Date", key: "sold_date" },
            { label: "Backup Text", key: "backup_text" },
            { label: "Backup File", key: "backup" },
            { label: "Action", key: "action" },
          ];
        } else {
          custom_header_fields.value = [
            { label: "Status", key: "status", thClass: "w-small" },
            { label: "Price Date", key: "price_date" },
            { label: "Leased Date", key: "sold_date" },
            { label: "List Price", key: "list_price" },
            { label: "Backup Text", key: "backup_text" },
            { label: "Leased Price", key: "sold_price" },
          ];
        }
      }
      break;

    case "/unit-report":
      if (current_user.value.role == "admin") {
        custom_header_fields.value = [
          {
            label: "Listing Type",
            key: "modified_listing_type",
            formatter: setListingType,
          },
          { label: "Status", key: "mls_status", thClass: "w-small" },
          { label: "List Price", key: "mls_list_price" },
          { label: "Beds", key: "beds" },
          { label: "Baths", key: "baths" },
          { label: "$PSF", key: "mls_psf" },
          { label: "Sold Price", key: "mls_sold_price" },
          { label: "SP SQFT", key: "mls_sp_sqft" },
          { label: "Sold Date", key: "mls_sold_date" },
          { label: "Collection Type", key: "collection_type" },
          { label: "Backup File", key: "backup_file" },
        ];
      } else {
        custom_header_fields.value = [
          {
            label: "Listing Type",
            key: "modified_listing_type",
            formatter: setListingType,
          },
          { label: "Status", key: "mls_status", thClass: "w-small" },
          { label: "List Price", key: "mls_list_price" },
          { label: "$PSF", key: "mls_psf" },
          { label: "Price Date", key: "price_date" },
          { label: "Incentives", key: "buyer_incentives" },
          { label: "Net Price", key: "net_price" },
          { label: "Net Price", key: "previous_net_price" },
          { label: "Net $PSF", key: "net_psf" },
          { label: "DOM", key: "mls_dom" },
          { label: "Sold Date", key: "mls_sold_date" },
        ];
      }
      break;

    case "/rental-report":
      if (current_user.value.role == "admin") {
        custom_header_fields.value = [
          { label: "Price Date", key: "modified_price_date" },
          { label: "List Price", key: "mls_list_price" },
          { label: "$PSF", key: "mls_psf" },
          { label: "Leased Date", key: "mls_sold_date" },
          { label: "Collection Type", key: "collection_type" },
          { label: "Backup File", key: "backup_file" },
          { label: "Status", key: "mls_status", thClass: "w-small" },
        ];
      } else {
        custom_header_fields.value = [
          { label: "Price Date", key: "modified_price_date" },
          { label: "List Price", key: "mls_list_price" },
          { label: "$PSF", key: "mls_psf" },
          { label: "Leased Date", key: "mls_sold_date" },
          { label: "Status", key: "mls_status", thClass: "w-small" },
        ];
      }
      break;

    default:
      if (props.listing_type == 'rental') {
        if (current_user.value.role == "admin") {
          custom_header_fields.value = [
            { label: "Status", key: "status", thClass: "w-small" },
            { label: "List Price", key: "list_price" },
            { label: "Price Date", key: "price_date" },
            { label: "Leased Price", key: "sold_price" },
            { label: "Leased Date", key: "sold_date" },
            { label: "Backup Text", key: "backup_text" },
            { label: "Backup File", key: "backup" },
            { label: "Action", key: "action" },
          ];
        } else {
          custom_header_fields.value = [
            { label: "Status", key: "status", thClass: "w-small" },
            { label: "Price Date", key: "price_date" },
            { label: "Leased Date", key: "sold_date" },
            { label: "List Price", key: "list_price" },
            { label: "Backup Text", key: "backup_text" },
            { label: "Leased Price", key: "sold_price" },
          ];
        }
      } else if (current_user.value.role == "admin") {
        custom_header_fields.value = [
          { label: "Status", key: "mls_status", thClass: "w-small" },
          { label: "Price Date", key: "mls_price_date" },
          { label: "DOM", key: "mls_dom" },
          { label: "List Price", key: "mls_list_price" },
          { label: "$PSF", key: "mls_psf" },
          { label: "Incentives", key: "buyer_incentives" },
          { label: "Net Price", key: "net_price" },
          { label: "Net $PSF", key: "net_psf" },
          { label: "Sold Date", key: "mls_sold_date" },
          { label: "Action", key: "action" },
          { label: "Backup File", key: "backup" },
        ];
      } else {
        custom_header_fields.value = [
          { label: "Status", key: "mls_status", thClass: "w-small" },
          { label: "Price Date", key: "mls_price_date" },
          { label: "DOM", key: "mls_dom" },
          { label: "List Price", key: "mls_list_price" },
          { label: "$PSF", key: "mls_psf" },
          { label: "Incentives", key: "buyer_incentives" },
          { label: "Net Price", key: "net_price" },
          { label: "Net $PSF", key: "net_psf" },
          { label: "Sold Date", key: "mls_sold_date" },
        ];
      }
      break;
  }
};
const setStatusColor = (status) => {
  let color = "";
  switch (status.toLowerCase()) {
    case "active":
      color = "#28a745";
      break;
    case "sold":
      color = "#dc3545";
      break;
    case "leased":
      color = "#dc3545";
      break;
    default:
      color = "#000000";
      break;
  }
  return color;
};
const lsmStatusClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'active') return 'sp-status--active';
  if (s === 'sold' || s === 'leased') return 'sp-status--sold';
  return 'sp-status--other';
};
const setColor = (pb) => {
  let color = "";
  let priceBackUp = "";

  if (route.currentRoute.path == "/mls-list")
    priceBackUp = props.data.field_price_back_up_value;
  else if (["/type", "/type-old", "/unit-report"].includes(route.currentRoute.path))
    priceBackUp = pb;
  else priceBackUp = props.mlsHistory[0].price_back_up;
  switch (priceBackUp) {
    case "Verified":
      color = "#0b36dc";
      break;
    case "Educated Estimated":
      color = "#dc3545";
      break;
    case "Semi-Verified":
      color = "#FFsss6F16";
      break;
  }
  return color;
};

const calculateResult = (data) => {
  let res;
  if (route.currentRoute.path == "/mls-list") {
    if (route.currentRoute.query["unittype[]"] == "2954")
      res = Number(
        Number(data.mls_list_price) / Number(data.mls_unit_size)
      ).toFixed(2);
    else
      res = Math.round(
        Number(Number(data.mls_list_price) / Number(data.mls_unit_size))
      );
  } else if (["/type", "/unit-mix"].includes(route.currentRoute.path)) {
    if (
      ["now leasing", "leased out"].includes(props.project_status.toLowerCase())
    )
      res = Number(data.mls_list_price / data.mls_unit_size).toFixed(2);
    else res = Math.round(Number(data.mls_list_price / data.mls_unit_size));
  } else {
    res = !props.isMarketStrata
      ? Number(data.mls_psf).toFixed(2).toLocaleString()
      : Math.round(data.mls_psf);
  }

  return res;
};
const getNetPrice = (data) => {
  const list_price = Math.round(data.mls_list_price);
  const incentive = Math.round(data.buyer_incentives);
  let res = list_price - incentive;
  return res ? res : "-";
};

const calculateNetPsf = (data) => {
  const net_price = getNetPrice(data);
  let result = Number(net_price / data.mls_unit_size);
  if (!props.isMarketStrata)
    result = Number(result.toFixed(2));
  else result = `${Math.round(Number(result))}`;

  return Number(result) > 0 ? `$${Number(result).toFixed(2)}` : "—";
};

const indexRow = ref(0);
const inPage = computed(() => {
  switch (route.currentRoute.path) {
    case "/mls-list":
      return "mls-list";
    case "/unit-report":
      return "unit-report";
    case "/rental-report":
      return "rental-report";
    default:
      return "utc";
  }
});
const setListingType = (value, key, item) => {
  let index = computed_mlsHistory.value.findIndex(
    (x) => !['mls-list', 'unit-report'].includes(inPage.value) ? x.rowIndex == item.rowIndex : x.entry == item.entry
  );
  let res = "";
  let priceDate = ["utc"].includes(inPage.value)
    ? new Date(item.mls_price_date)
    : new Date(item.price_date);
  let soldDate = new Date(item.mls_sold_date);
  let constructionCompletionDate = ["utc"].includes(inPage.value)
    ? new Date(props.occupancy_date)
    : new Date(props.reportHistoryData.occupancy_date);
  let soldOutDate = ["utc"].includes(inPage.value)
    ? new Date(props.sold_out_date)
    : new Date(props.reportHistoryData.sold_out_date);
  let hasSoldStatusBeforeEntry = false;
  if (index > 0) {
    for (let i = index - 1; i >= 0; i--) {
      if (computed_mlsHistory.value[i].mls_status.toLowerCase() == "sold") {
        hasSoldStatusBeforeEntry = true;
        break;
      }
    }
  } else {
    hasSoldStatusBeforeEntry = false;
  }

  res = "Presale";

  if (hasSoldStatusBeforeEntry) {
    if ([priceDate, soldDate].some((date) => date < constructionCompletionDate))
      res = "Assignment";

    if ([priceDate, soldDate].some((date) => date > constructionCompletionDate))
      res = "Resale";
  } else {
    if (
      [priceDate, soldDate].some(
        (date) => date > soldOutDate && date < constructionCompletionDate
      )
    )
      res = "Assignment";

    if (
      [priceDate, soldDate].some(
        (date) => date > soldOutDate && date > constructionCompletionDate
      )
    )
      res = "Resale";
  }

  if (inPage.value == "utc") {
    if (
      ["now leasing", "leased out"].includes(props.project_status.toLowerCase())
    ) {
      switch (res) {
        case "Presale":
        case "Assignment": // rentals have no assignments
          res = "New Lease";
          break;

        case "Resale":
          res = "Re-Lease";
          break;
      }
    }
  } else {
    if (!props.isMarketStrata) {
      switch (res) {
        case "Presale":
        case "Assignment": // rentals have no assignments
          res = "New Lease";
          break;

        case "Resale":
          res = "Re-Lease";
          break;
      }
    }
  }

  return res;
};

const setRentalListingType = (value, key, item) => {

  let index = item.entry
  let res = "";
  let priceDate = new Date(item.price_date);
  let soldDate = new Date(item.sold_date);
  let constructionCompletionDate = new Date(props.occupancy_date)
  let soldOutDate = new Date(props.sold_out_date)
  let hasSoldStatusBeforeEntry = false;
  if (index > 0) {
    for (let i = index - 1; i >= 0; i--) {
      if (computed_mlsHistory.value[i].status.toLowerCase() == "sold") {
        hasSoldStatusBeforeEntry = true;
        break;
      }
    }
  } else {
    hasSoldStatusBeforeEntry = false;
  }

  res = "Presale";
  if (hasSoldStatusBeforeEntry) {
    if ([priceDate, soldDate].some((date) => date < constructionCompletionDate))
      res = "Assignment";

    if (([priceDate, soldDate].some((date) => date > constructionCompletionDate)) || index > 0)
      res = "Resale";
  } else {
    if (
      [priceDate, soldDate].some(
        (date) => date > soldOutDate && date < constructionCompletionDate
      )
    )
      res = "Assignment";

    if (([priceDate, soldDate].some(
      (date) => date > soldOutDate && date > constructionCompletionDate
    ))
    )
      res = "Resale";
  }

  if (inPage.value == "utc") {
    if (
      ["now leasing", "leased out"].includes(props.project_status.toLowerCase())
    ) {
      switch (res) {
        case "Presale":
        case "Assignment": // rentals have no assignments
          res = "New Lease";
          break;

        case "Resale":
          res = "Re-Lease";
          break;
      }
    }
  } else {
    if (!props.isMarketStrata) {
      switch (res) {
        case "Presale":
        case "Assignment": // rentals have no assignments
          res = "New Lease";
          break;

        case "Resale":
          res = "Re-Lease";
          break;
      }
    }
  }

  return res;
};

const isRealValue = (value) => {
  return !["", "-", NaN, 0, null, "NaN", 0.00, '0.00'].includes(value);
};
const getDefaultValue = (data) => {
  let res = "";


  if (isRealValue(data.value) === false) return (res = "—");

  if (["mls_psf",
    "net_psf",
  ].includes(data.field.key)) {
    res = `$${Number(data.value).toFixed(2)}`
  } else if ([
    "mls_list_price",
    "buyer_incentives",
    "realtor_incentives",
    "net_price"
  ].includes(data.field.key)) {
    res = `$${Math.round(parseInt(data.value)).toLocaleString("en-US")}`
  } else {
    res = data.value;
  }
  return isRealValue(res) ? res : '—'
};
const getPriceChange = (oldValue, newValue) => {
  let oldToNum = Number(oldValue);
  let newToNum = Number(newValue);
  let result = {
    diffPercentage: "",
    color: "",
    icon: "",
  };

  if (newValue == "-" || !oldValue)
    return (result = {
      diffPercentage: "",
      color: "",
      icon: "",
    });
  if (oldValue == null) result.diffPercentage = 0;
  else
    result.diffPercentage = Number(
      ((newToNum - oldToNum) / oldToNum) * 100
    ).toFixed(2);

  if (Number(result.diffPercentage) > 0) {
    result.color = "green";
    result.icon = "fa-long-arrow-alt-up";
  } else if (Number(result.diffPercentage) < 0) {
    result.color = "red";
    result.icon = "fa-long-arrow-alt-down";
  } else {
    result.diffPercentage = 0;
    result.color = "black";
    result.icon = "minus";
  }

  return result;
};
onMounted(() => {
  if (
    ["/mls-list", "/type", "/type-old", "/unit-mix", "/unit-report", "/rental-report"].includes(
      route.currentRoute.path.toLocaleLowerCase()
    )
  )
    setHeaderFields();
});
</script>

<style lang="scss">
/* b-modal chrome reset — lets SummaryPopUp's own sp-card stand alone */
.sp-bare-modal .modal-content {
  background: transparent;
  border: 0;
  box-shadow: none;
  border-radius: 10px;
  overflow: visible;
}

.sp-bare-modal .modal-body {
  padding: 0 !important;
}

/* ── Summary Popup (matches Listing Summary Modal design) ── */
.sp-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #E9E8E9;
  display: flex;
  flex-direction: column;
}

/* Header */
.sp-hdr {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 18px;
  background: #12283F;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
  flex-wrap: wrap;
}

.sp-hdr__rule {
  width: 22px;
  height: 3px;
  background: #FC5868;
  border-radius: 2px;
  flex-shrink: 0;
}

.sp-hdr__title {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #fff;
  white-space: nowrap;
}

.sp-hdr__sep {
  width: 1px;
  height: 16px;
  background: rgba(255, 255, 255, 0.18);
}

.sp-hdr__unit {
  font-family: Poppins, sans-serif;
  color: rgba(255, 255, 255, 0.82);
  font-size: 13px;
  font-weight: 400;
}

.sp-hdr__unit b {
  color: #fff;
  font-weight: 600;
}

.sp-unit-link {
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.sp-unit-link:hover {
  opacity: .8;
}

.sp-hdr__date {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.sp-hdr__date-lbl {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 10px;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.55);
}

.sp-hdr__date-val {
  color: #fff;
  font-weight: 500;
  font-size: 13px;
}

.sp-add-entry {
  height: 26px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #FC5868;
  background: #FC5868;
  color: #fff;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.12s;
}

.sp-add-entry:hover {
  background: #e54b5b;
  border-color: #e54b5b;
}

.sp-add-entry svg {
  width: 11px;
  height: 11px;
}

.sp-hdr__close {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: all 0.12s;
}

.sp-hdr__close:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.32);
}

.sp-hdr__close svg {
  width: 13px;
  height: 13px;
}

/* Specs strip */
.sp-specs {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  background: #F7F8FB;
  border-bottom: 1px solid #E9E8E9;
  flex-shrink: 0;
  overflow-x: auto;
}

.sp-specs--wide {
  grid-template-columns: repeat(11, minmax(0, 1fr));
}

.sp-spec {
  padding: 10px 12px;
  border-right: 1px solid #E9E8E9;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.sp-spec:last-child {
  border-right: 0;
}

.sp-spec--wide {
  grid-column: span 2;
}

.sp-spec__lbl {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: #343376;
}

.sp-spec__val {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #12283F;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sp-spec__muted {
  color: #8A8A8A;
  font-weight: 400;
}

/* Table wrapper */
.sp-table-wrap {
  background: #fff;
  overflow: auto;
  flex: 1;
}

/* b-table overrides — apply lsm-table look */
.sp-table.b-table,
.sp-table.b-table-sm {
  font-family: Poppins, sans-serif;
  font-size: 12px;
  color: #12283F;
  margin-bottom: 0;
}

.sp-table.b-table thead th {
  background: #fff !important;
  color: #545454 !important;
  text-align: left;
  padding: 9px 12px !important;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 10px !important;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid #C9CCD2 !important;
  border-top: 0 !important;
  white-space: nowrap;
  vertical-align: middle;
}

.sp-table.b-table tbody td {
  padding: 0 12px !important;
  height: 36px;
  border-top: 0 !important;
  border-bottom: 1px solid #EEF0F4 !important;
  white-space: nowrap;
  vertical-align: middle;
  font-size: 12px;
  color: #12283F;
}

.sp-table.b-table tbody tr:hover td {
  background: #EDF2FE !important;
}

.sp-table.b-table tbody tr:last-child td {
  border-bottom: 0 !important;
}

/* Soften the b-table row variants (active=success, sold=danger) */
.sp-table.b-table tbody tr.table-success td,
.sp-table.b-table tbody tr.b-table-row-success td {
  background-color: rgba(31, 157, 85, 0.06) !important;
  color: #12283F;
}

.sp-table.b-table tbody tr.table-danger td,
.sp-table.b-table tbody tr.b-table-row-danger td {
  background-color: rgba(212, 56, 71, 0.06) !important;
  color: #12283F;
}

.sp-table.b-table tbody tr.table-success:hover td,
.sp-table.b-table tbody tr.b-table-row-success:hover td {
  background-color: rgba(31, 157, 85, 0.12) !important;
}

.sp-table.b-table tbody tr.table-danger:hover td,
.sp-table.b-table tbody tr.b-table-row-danger:hover td {
  background-color: rgba(212, 56, 71, 0.12) !important;
}

.sp-muted {
  color: #8A8A8A;
}

/* Type pill */
/* Listing Type pill: see assets/styles/listing-type-pill.css (global, shared
   with the other tables that render the column). */

/* Status */
.sp-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
  font-size: 12px;
}

.sp-status::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 999px;
  flex-shrink: 0;
}

.sp-status--active {
  color: #1F9D55;
}

.sp-status--active::before {
  background: #1F9D55;
}

.sp-status--sold {
  color: #D43847;
}

.sp-status--sold::before {
  background: #D43847;
}

.sp-status--other {
  color: #8A8A8A;
}

.sp-status--other::before {
  background: #C9CCD2;
}

/* Delta */
.sp-delta {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 999px;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.sp-delta svg {
  width: 9px;
  height: 9px;
}

.sp-delta--down {
  background: rgba(212, 56, 71, 0.10);
  color: #D43847;
}

.sp-delta--up {
  background: rgba(31, 157, 85, 0.10);
  color: #1F9D55;
}

/* Actions */
.sp-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  padding: 2px 4px;
  transition: opacity 0.12s;
}

.sp-action:hover {
  opacity: 0.75;
}

.sp-action--edit {
  color: #343376;
}

.sp-action--del {
  color: #D43847;
}

.sp-action-sep {
  color: #C9CCD2;
  margin: 0 4px;
}

.sp-backup-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #343376;
  font-size: 13px;
  padding: 2px 4px;
  transition: opacity 0.12s;
}

.sp-backup-link:hover {
  opacity: 0.75;
  color: #343376;
}

.sp-approve-btn {
  background: transparent;
  border: 1px solid #343376;
  color: #343376;
  padding: 2px 10px;
  border-radius: 999px;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.12s;
}

.sp-approve-btn:hover {
  background: #343376;
  color: #fff;
}

/* Responsive */
@media (max-width: 992px) {

  .sp-specs,
  .sp-specs--wide {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 576px) {
  .sp-hdr {
    padding: 11px 14px;
    gap: 10px;
  }

  .sp-hdr__date {
    margin-left: 0;
  }

  .sp-specs,
  .sp-specs--wide {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sp-spec--wide {
    grid-column: span 2;
  }
}

/* ── Dark mode ── */
html.dark .sp-card {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .sp-specs {
  background: #0E1B2C;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .sp-spec {
  border-color: rgba(255, 255, 255, 0.07);
}

html.dark .sp-spec__lbl {
  color: #a5a8e8;
}

html.dark .sp-spec__val {
  color: #d6e4f0;
}

html.dark .sp-spec__muted {
  color: rgba(214, 228, 240, 0.4);
}

html.dark .sp-table-wrap {
  background: #152030;
}

html.dark .sp-table.b-table {
  color: #d6e4f0;
}

html.dark .sp-table.b-table thead th {
  background: #0E1B2C !important;
  color: rgba(214, 228, 240, 0.55) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

html.dark .sp-table.b-table tbody td {
  color: #d6e4f0;
  border-color: rgba(255, 255, 255, 0.05) !important;
}

html.dark .sp-table.b-table tbody tr:hover td {
  background: #1d3048 !important;
}

html.dark .sp-table.b-table tbody tr.table-success td,
html.dark .sp-table.b-table tbody tr.b-table-row-success td {
  background-color: rgba(31, 157, 85, 0.10) !important;
  color: #d6e4f0;
}

html.dark .sp-table.b-table tbody tr.table-danger td,
html.dark .sp-table.b-table tbody tr.b-table-row-danger td {
  background-color: rgba(212, 56, 71, 0.10) !important;
  color: #d6e4f0;
}

html.dark .sp-muted {
  color: rgba(214, 228, 240, 0.35);
}

html.dark .sp-add-entry {
  background: #FC5868;
  border-color: #FC5868;
  color: #fff;
}

html.dark .sp-add-entry:hover {
  background: #e54b5b;
  border-color: #e54b5b;
}

html.dark .sp-action--edit,
html.dark .sp-backup-link,
html.dark .sp-backup-link:hover {
  color: #a5a8e8;
}

html.dark .sp-action--del {
  color: #ff8b96;
}

html.dark .sp-action-sep {
  color: rgba(255, 255, 255, 0.18);
}

html.dark .sp-approve-btn {
  border-color: #a5a8e8;
  color: #a5a8e8;
}

html.dark .sp-approve-btn:hover {
  background: #a5a8e8;
  color: #152030;
}
</style>
